/**
 * One order, priced: handover rev7 §5.4, §5.6, §5.11, §6.6, §6.7 and §12,
 * composed out of `quantity.ts` and `money.ts`.
 *
 * The engine reports problems by code. The copy for each belongs to the page,
 * which carries every string in both languages (§5.12); a rule that decided
 * its own wording here would have one of them and be wrong in the other.
 */

import { add, divide, rational, ZERO, type Rational } from './rational.ts'
import {
  allocateSide,
  dishCountSuggestion,
  drinkQuantities,
  fruitQuantities,
  piecesPerGuest,
  type SideAllocation,
  type SideLine,
} from './quantity.ts'
import {
  categoryTotal,
  foodTotal,
  type CategoryTotal,
  type FoodTotal,
} from './money.ts'
import type {
  EngineParams,
  OrderInput,
  OrderLine,
  PriceCategory,
  QuantityParams,
  Side,
} from './types.ts'

/** §5.11 and `public.price_category`. */
export const CATEGORY_OF_SIDE: Readonly<Record<Side, PriceCategory>> = {
  sav: 'sos',
  sw: 'edes',
}

/** The order §5.11 lists the category rows in. */
export const CATEGORY_ORDER: readonly PriceCategory[] = ['sos', 'edes', 'gyumolcs', 'ital']

export type ProblemCode =
  /** §5.2, §19.8 no. 25: more gluten-free guests than guests. Refused. */
  | 'gluten_free_above_guests'
  /** §5.2: the headcount has to be at least one. */
  | 'guests_below_one'
  /** §6.7 step 6: a side has gluten-free guests and no eligible dish that day.
   *  Informational since fix list item 2 (Barna, 10 October 2026): it never
   *  blocks the order; the page says which side has no gluten-free dish. */
  | 'gluten_free_unavailable'
  /** §6.6: a line falls below the catalogue minimum production quantity. */
  | 'below_minimum_production'
  /** §6.6: more dishes on a side than the page limit allows. */
  | 'above_page_limit'
  /** §5.6: more fruit items than `max_fruit_varieties`. */
  | 'above_fruit_limit'

export interface Problem {
  code: ProblemCode
  /** True where the order cannot go ahead on the automatic lane. */
  blocking: boolean
  side?: Side
  dishId?: string
  /** The limit or the figure the rule was measured against, where there is one. */
  limit?: number
  actual?: number
}

export interface PerGuestPieces {
  savoury: Rational
  sweet: Rational
  total: Rational
}

export interface PricedOrder {
  guests: number
  glutenFreeGuests: number
  /** §6.7 step 1: the recommendation, before margin and rounding. */
  recommended: PerGuestPieces
  /**
   * §5.4: what is actually made, divided by the **whole** guest count,
   * gluten-free portions included. This is the figure the page prints.
   */
  perGuest: PerGuestPieces
  sides: Record<Side, SideAllocation>
  fruitLines: OrderLine[]
  drinkLines: OrderLine[]
  /** Every line, in §5.11's category order. */
  lines: OrderLine[]
  categories: CategoryTotal[]
  food: FoodTotal
  /** §6.6: the dish-count suggestion each side opened with. */
  dishCounts: Record<Side, ReturnType<typeof dishCountSuggestion>>
  problems: Problem[]
}

export const isBlocked = (problems: readonly Problem[]): boolean =>
  problems.some((problem) => problem.blocking)

/** What is made of each dish on a side, its main and gluten-free lines together. */
function dishTotals(allocation: SideAllocation): Map<string, number> {
  const totals = new Map<string, number>()
  for (const line of allocation.all) {
    totals.set(line.dish.id, (totals.get(line.dish.id) ?? 0) + line.portions)
  }
  return totals
}

function lineFrom(
  line: SideLine,
  category: PriceCategory,
  suggestedPortions: number,
  made: number = line.portions,
): OrderLine {
  return {
    dishId: line.dish.id,
    priceCategory: category,
    isGlutenFreePortion: false,
    /* Run 17 item B5(b): the gluten-free guests' share of this line, where
       the dish is on the menu and carries it. */
    glutenFreePortions: line.glutenFreePortions ?? 0,
    isDrink: false,
    suggestedPortions,
    orderedPortions: line.portions,
    recommendedPortions: line.recommendedPortions,
    /* Run 15 item 1(j): a line the customer counted out with − / + is stored
       as the customer's, and a later re-pricing leaves it where it is. */
    qtySource: line.customerSet ? 'customer' : 'recommendation',
    unit: line.dish.unit,
    productionUnits: line.productionUnits,
    lineNet: line.portions * line.dish.netPrice,
    share: line.share,
    shareWithMargin: line.shareWithMargin,
    marginPct: line.marginPct,
    belowMinimumProduction: line.dish.minProduction !== null && made < line.dish.minProduction,
  }
}

/**
 * §6.7: `suggested_portions` records each line at *javasolt*. The quantity
 * level that word named is retired (`build/FIXES.md` item 2), and the
 * savoury/sweet balance of step 01 took its place with the same middle
 * choice, *Javasolt* — so the suggested quantity is the line at the
 * recommended balance and the ordered quantity is the line at the balance the
 * customer chose. The pair is what §6.7 keeps them for: it lets calibration
 * see how far customers move from the recommendation.
 */
function suggestedPortionsIndex(input: OrderInput, params: EngineParams): Map<string, number> {
  const index = new Map<string, number>()
  /* Run 15 item 1: *javasolt* is the line with neither the balance nor the
     customer's own − / + on it, so calibration can see how far both move an
     order. With neither in play it is the ordered quantity itself. */
  const quantities = input.quantities ?? {}
  if (input.balance === 'rec' && Object.keys(quantities).length === 0) return index
  const recommended = allocateBothSides({ ...input, balance: 'rec', quantities: {} }, params)
  for (const side of ['sav', 'sw'] as const) {
    for (const line of recommended[side].lines) {
      index.set(`${side}:${line.dish.id}`, line.portions)
    }
    for (const line of recommended[side].glutenFreeLines) {
      index.set(`${side}:gf:${line.dish.id}`, line.portions)
    }
  }
  return index
}

function allocateBothSides(input: OrderInput, params: EngineParams): Record<Side, SideAllocation> {
  const pieces = piecesPerGuest(input.occasion, input, params.quantity)
  const eligible = input.glutenFreeEligible ?? []
  const allocate = (side: Side): SideAllocation =>
    allocateSide(
      {
        dishes: side === 'sav' ? input.savouryDishes : input.sweetDishes,
        piecesPerGuest: side === 'sav' ? pieces.savoury : pieces.sweet,
        guests: input.guests,
        glutenFreeGuests: input.glutenFreeGuests,
        quantities: input.quantities,
        glutenFreeEligible: eligible.filter((dish) => dish.side === side),
      },
      params.quantity,
    )
  return { sav: allocate('sav'), sw: allocate('sw') }
}

function collectProblems(
  input: OrderInput,
  sides: Record<Side, SideAllocation>,
  dishCounts: Record<Side, ReturnType<typeof dishCountSuggestion>>,
  params: QuantityParams,
): Problem[] {
  const problems: Problem[] = []
  if (input.guests < 1) {
    problems.push({ code: 'guests_below_one', blocking: true, actual: input.guests })
  }
  /* §19.8 no. 25: gluten-free guests above the guest count is refused, not
     silently clamped. The allocation clamps so that a refused order still
     produces figures to show beside the message. */
  if (input.glutenFreeGuests > input.guests) {
    problems.push({
      code: 'gluten_free_above_guests',
      blocking: true,
      limit: input.guests,
      actual: input.glutenFreeGuests,
    })
  }
  for (const side of ['sav', 'sw'] as const) {
    const allocation = sides[side]
    if (allocation.glutenFreeUnavailable) {
      problems.push({ code: 'gluten_free_unavailable', blocking: false, side })
    }
    const chosen = side === 'sav' ? input.savouryDishes : input.sweetDishes
    const limit = dishCounts[side].limit
    if (chosen.length > limit) {
      problems.push({
        code: 'above_page_limit',
        blocking: true,
        side,
        limit,
        actual: chosen.length,
      })
    }
    /* §6.6: the page asks the customer to choose fewer dishes; it never
       rounds a line up to the minimum batch silently. Only the dishes the
       customer chose are measured — the gluten-free portions are Planteen's
       own composition and are not the customer's to reduce. */
    /* Run 9, item 5: a dish in the gluten-free box is made once, main and
       box together, so its minimum is measured on that total. */
    const totals = dishTotals(allocation)
    for (const line of allocation.lines) {
      const made = totals.get(line.dish.id) ?? line.portions
      if (line.dish.minProduction !== null && made < line.dish.minProduction) {
        problems.push({
          code: 'below_minimum_production',
          blocking: true,
          side,
          dishId: line.dish.id,
          limit: line.dish.minProduction,
          actual: made,
        })
      }
    }
  }
  const fruit = input.fruit ?? []
  if (fruit.length > params.maxFruitVarieties) {
    problems.push({
      code: 'above_fruit_limit',
      blocking: true,
      limit: params.maxFruitVarieties,
      actual: fruit.length,
    })
  }
  return problems
}

/** §6.7, §5.6, §5.11: one order, from the customer's inputs to its category rows. */
export function priceOrder(input: OrderInput, params: EngineParams): PricedOrder {
  const recommendedPieces = piecesPerGuest(input.occasion, input, params.quantity)
  const sides = allocateBothSides(input, params)
  const suggested = suggestedPortionsIndex(input, params)
  const dishCounts: Record<Side, ReturnType<typeof dishCountSuggestion>> = {
    sav: dishCountSuggestion(input.occasion, input.guests, 'sav', params.quantity),
    sw: dishCountSuggestion(input.occasion, input.guests, 'sw', params.quantity),
  }

  const sideLines = (side: Side): OrderLine[] => {
    const category = CATEGORY_OF_SIDE[side]
    const allocation = sides[side]
    const totals = dishTotals(allocation)
    const chosen = allocation.lines.map((line) =>
      lineFrom(
        line,
        category,
        suggested.get(`${side}:${line.dish.id}`) ?? line.portions,
        totals.get(line.dish.id),
      ),
    )
    const glutenFree = allocation.glutenFreeLines.map((line) => ({
      ...lineFrom(
        line,
        category,
        suggested.get(`${side}:gf:${line.dish.id}`) ?? line.portions,
        totals.get(line.dish.id),
      ),
      isGlutenFreePortion: true,
    }))
    return [...chosen, ...glutenFree]
  }

  const savouryLines = sideLines('sav')
  const sweetLines = sideLines('sw')

  const fruitLines: OrderLine[] = fruitQuantities(
    input.guests,
    input.fruit ?? [],
    params.quantity,
  ).map((entry) => ({
    dishId: entry.item.id,
    priceCategory: 'gyumolcs' as const,
    isGlutenFreePortion: false,
    glutenFreePortions: 0,
    isDrink: false,
    suggestedPortions: entry.portions,
    orderedPortions: entry.portions,
    recommendedPortions: entry.portions,
    /* §5.6: fruit quantities are Planteen's, never the customer's. */
    qtySource: 'recommendation' as const,
    /* §5.11 *Quantities*: *adag* for salads, vegetable sticks and fruit. */
    unit: 'adag',
    productionUnits: entry.productionUnits,
    lineNet: entry.portions * entry.item.netPrice,
    share: rational(entry.portions),
    shareWithMargin: rational(entry.portions),
    marginPct: ZERO,
    belowMinimumProduction: false,
  }))

  const drinkLines: OrderLine[] = drinkQuantities(input.guests, input.drinks ?? []).map((entry) => ({
    dishId: entry.drink.id,
    priceCategory: 'ital' as const,
    isGlutenFreePortion: false,
    glutenFreePortions: 0,
    isDrink: true,
    suggestedPortions: entry.portions,
    orderedPortions: entry.portions,
    recommendedPortions: entry.portions,
    /* Run 15 item 2(a): a drink the customer counted out in bottles is the
       customer's; the lines the system adds for itself (F3, F7) are not. */
    qtySource: entry.drink.customerSet === true ? ('customer' as const) : ('recommendation' as const),
    unit: 'db',
    productionUnits: entry.productionUnits,
    lineNet: entry.portions * entry.drink.netPrice,
    share: rational(entry.portions),
    shareWithMargin: rational(entry.portions),
    marginPct: ZERO,
    belowMinimumProduction: false,
  }))

  const byCategory: Record<PriceCategory, OrderLine[]> = {
    sos: savouryLines,
    edes: sweetLines,
    gyumolcs: fruitLines,
    ital: drinkLines,
  }
  const categories = CATEGORY_ORDER.filter((category) => byCategory[category].length > 0).map(
    (category) => categoryTotal(category, byCategory[category], params.money.vatPct),
  )

  const guests = rational(Math.max(input.guests, 1))
  const perSide = (side: Side): Rational => divide(rational(sides[side].portions), guests)
  const perGuest: PerGuestPieces = {
    savoury: perSide('sav'),
    sweet: perSide('sw'),
    total: divide(rational(sides.sav.portions + sides.sw.portions), guests),
  }

  return {
    guests: input.guests,
    glutenFreeGuests: input.glutenFreeGuests,
    recommended: {
      savoury: recommendedPieces.savoury,
      sweet: recommendedPieces.sweet,
      total: add(recommendedPieces.savoury, recommendedPieces.sweet),
    },
    perGuest,
    sides,
    fruitLines,
    drinkLines,
    lines: CATEGORY_ORDER.flatMap((category) => byCategory[category]),
    categories,
    food: foodTotal(categories),
    dishCounts,
    problems: collectProblems(input, sides, dishCounts, params.quantity),
  }
}
